import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { importDB } from 'dexie-export-import'
import JSZip from 'jszip'
import { DownloadInfoProps, setDownloadState } from 'redux/reducers/downloadReducer'
import { saveImageToDb } from 'database/queries/offline'

type DownloadURLType = {
  [key: string]: string
}

type DownloadResult = {
  startDownloads: (downloadUrls: DownloadURLType) => void
}

const useDownloads = (): DownloadResult => {
  const dispatch = useDispatch()
  const [controllers, setControllers] = useState<AbortController[]>([])

  useEffect(() => {
    return () => {
      controllers.forEach((controller) => {
        controller.abort()
      })
    }
  }, [controllers])

  const updateDownloadInfo = (key: string, data: Partial<DownloadInfoProps>): void => {
    dispatch(setDownloadState({ key, data }))
  }

  const handleJson = async (blobData: Uint8Array[]) => {
    try {
      const blob = new Blob(blobData, { type: 'application/json' })
      await importDB(blob)
    } catch (error) {
      console.error('Handle json error', error)
    }
  }

  const handleImage = async (blobData: Uint8Array[]) => {
    // Step 1: Unzip the file
    const blob = new Blob(blobData)

    const zip = new JSZip()
    const unzipFiles = await zip.loadAsync(blob)

    // Step 2: Iterate over each file in the zip and store it in the IndexedDB
    let counter = 0

    for (const filename in unzipFiles.files) {
      if (counter >= 1000) {
        break
      }

      const file = unzipFiles.files[filename]

      if (!file.dir) {
        const content = await file.async('blob')
        await saveImageToDb(filename, content)
        counter++
      }
    }
  }

  const startDownload = async (
    key: string,
    url: string,
    controller: AbortController,
  ): Promise<void> => {
    updateDownloadInfo(key, { url, progress: 0, downloadedSize: 0, error: false })

    try {
      const response = await fetch(url, { signal: controller.signal })
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`)
      }

      const contentLength = response.headers.get('content-length')
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')

      const downloadState = {
        fileName: key,
        downloadedSize: Number(contentLength),
      }
      updateDownloadInfo(key, downloadState)

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      const chunks: Uint8Array[] = []
      let receivedLength = 0

      const pump = async (): Promise<void> => {
        const { done, value } = await reader.read()
        if (controller.signal.aborted) {
          reader.cancel()
          return
        }
        if (done) {
          if (isJson) {
            await handleJson(chunks)
          } else {
            await handleImage(chunks)
          }
          return
        }
        chunks.push(value)
        receivedLength += value.length
        const progress = (receivedLength / Number(contentLength)) * 100
        updateDownloadInfo(key, { progress, downloadedSize: receivedLength })
        await pump()
      }
      await pump()
      updateDownloadInfo(key, { isDownload: true })
    } catch (error) {
      updateDownloadInfo(key, { isDownload: false, error: true })
    }
  }

  const startDownloads = async (downloadUrls: DownloadURLType): Promise<void> => {
    const promises = Object.entries(downloadUrls).map(([key, url]) => {
      const controller = new AbortController()
      setControllers((prevControllers) => [...prevControllers, controller])
      return startDownload(key, url, controller)
    })

    await Promise.all(promises)
  }

  return {
    startDownloads,
  }
}

export default useDownloads
